import { canManageClientDocument } from "../utils/clientContactPermissions";
import { freshModal } from "../components/freshModal";
import { useState, useEffect } from "react";
import { message } from "antd";
import {
  FileText,
  Building2,
  Plus,
  Trash2,
  Eye,
  Save,
  Loader2,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Gift,
  Download,
  X,
} from "lucide-react";
import axiosInstance from "../axios/AxiosInstance";
import { Label } from "../components/reusableComponents/Label";
import { Input } from "../components/reusableComponents/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/reusableComponents/Select";
import { Button } from "../components/reusableComponents/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/reusableComponents/Dialog";
import qualifications from "../utils/qualifications";
import LazyLoadingSelectQualification from "../components/reusableComponents/LazyLoadingSelectQualification";
import {
  usesQuantity,
  pricingMultiplier,
  lineAmount,
} from "../utils/quotationPricing";
import { TYPES_PRIMES } from "../config/apiEndpoints";
import { generatePDF } from "../utils/generatePDFUtils";

const CreateQuotationModalFormSession = ({
  client,
  isCreateQuotation,
  setIsCreateQuotation,
  onCreated,
  initialQuotation,
}) => {
  // États principaux
  const [currentStep, setCurrentStep] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [quotation, setQuotation] = useState({
    client: client,
    addressFacturation: "",
    addressLivraison: "",
    contact: "",
    products:
      initialQuotation?.products?.map((product) => ({ ...product })) || [],
    ...(initialQuotation
      ? {
          addressFacturation: initialQuotation.addressFacturation || "",
          addressLivraison: initialQuotation.addressLivraison || "",
          contact: initialQuotation.contact || "",
          expiryDate: initialQuotation.expiryDate,
        }
      : {}),
  });

  // États pour l'ajout de services
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedPoste, setSelectedPoste] = useState("");
  const [taux, setTaux] = useState("");
  const [coefficient, setCoefficient] = useState("");
  const [quantity, setQuantity] = useState("1");
  const quantityPricing = ["Placement", "Vacation"].includes(selectedProduct);
  const [comment, setComment] = useState("");
  const [minimumHours, setMinimumHours] = useState("");

  // États pour les primes
  const [selectedPrimeType, setSelectedPrimeType] = useState("");
  const [primeDescription, setPrimeDescription] = useState("");
  const [primeTaux, setPrimeTaux] = useState("");
  const [primeCoefficient, setPrimeCoefficient] = useState("");
  const [primeComment, setPrimeComment] = useState("");

  // États pour la prévisualisation
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // Configuration
  const services = ["Délégation", "Gestion", "Placement", "Vacation"];
  const totalSteps = 5; // Ajout d'une étape pour les primes

  // Préparation des données client
  const clientAddresses = [
    `${client?.address} ${client?.postCode} ${client?.town}`.toUpperCase(),
    `Livraison : ${client?.addressLivraison} ${client?.postCodeLivraison} ${client?.townLivraison}`.toUpperCase(),
    `Facturation : ${client?.addressFacturation} ${client?.postCodeFacturation} ${client?.townFacturation}`.toUpperCase(),
  ].filter((addr) => !addr.includes("undefined") && addr.trim().length > 10);

  const clientContacts =
    client?.contacts?.filter((contact) => canManageClientDocument(contact, "quotations")).map((contact, idx) => {
      const contactInfo = [
        contact.title,
        contact.firstname,
        contact.lastname,
        contact.email,
        contact.mobile,
      ]
        .filter(Boolean)
        .join(" ");
      return contactInfo || `Contact ${idx + 1}`;
    }) || [];

  useEffect(() => {
    if (client) {
      setQuotation((prev) => ({ ...prev, client }));
    }
  }, [client]);

  // Fonction de prévisualisation avec PDF
  const generatePreview = async () => {
    if (quotation.products.length === 0) {
      message.error(
        "Ajoutez au moins un service ou une prime avant la prévisualisation",
      );
      return;
    }

    setIsGeneratingPreview(true);
    try {
      // Créer un devis temporaire pour la prévisualisation avec données d'agence fictives
      const tempQuotation = {
        ...quotation,
        quotationNumber: "PREVIEW",
        createdAt: new Date(),
        client: client,
        agency: {
          name: "AGENCE PREVIEW",
          address: "123 Rue de la Prévisualisation",
          postCode: "75001",
          town: "Paris",
          siret: "12345678901234",
          TVA: "FR12345678901",
          phone: "01 23 45 67 89",
          email: "contact@agence.com",
        },
      };

      // Générer le PDF en mode visualisation
      generatePDF(tempQuotation, true, setIsGeneratingPreview);

      message.success("Prévisualisation PDF générée");
    } catch (error) {
      console.error(
        "Erreur lors de la génération de la prévisualisation:",
        error,
      );
      message.error("Erreur lors de la génération de la prévisualisation PDF");
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Fonction de téléchargement PDF
  const downloadPreviewPDF = async () => {
    if (quotation.products.length === 0) {
      message.error(
        "Ajoutez au moins un service ou une prime avant le téléchargement",
      );
      return;
    }

    setIsDownloadingPDF(true);
    try {
      // Créer un devis temporaire pour le téléchargement
      const tempQuotation = {
        ...quotation,
        quotationNumber: "PREVIEW",
        createdAt: new Date(),
        client: client,
        agency: {
          name: "AGENCE PREVIEW",
          address: "123 Rue de la Prévisualisation",
          postCode: "75001",
          town: "Paris",
          siret: "12345678901234",
          TVA: "FR12345678901",
          phone: "01 23 45 67 89",
          email: "contact@agence.com",
        },
      };

      // Générer le PDF en mode téléchargement
      generatePDF(tempQuotation, false, setIsDownloadingPDF);

      message.success("PDF téléchargé avec succès");
    } catch (error) {
      console.error("Erreur lors du téléchargement du PDF:", error);
      message.error("Erreur lors du téléchargement du PDF");
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  // Validation par étape
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return quotation.client ? [] : ["Client requis"];
      case 2:
        const errors = [];
        if (!quotation.addressFacturation)
          errors.push("Adresse de facturation");
        if (!quotation.addressLivraison) errors.push("Lieu de mission");
        return errors;
      case 3:
        return quotation.contact ? [] : ["Contact requis"];
      case 4:
        return quotation.products.filter((p) => !p.isPrime).length > 0
          ? []
          : ["Au moins un service requis"];
      case 5:
        // Étape primes - optionnelle
        return [];
      default:
        return [];
    }
  };

  // Navigation entre étapes
  const nextStep = () => {
    const errors = validateStep(currentStep);
    if (errors.length > 0) {
      message.error(`Champs requis : ${errors.join(", ")}`);
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 2) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Gestion des services
  const addService = () => {
    const errors = [];
    if (!selectedProduct) errors.push("Type de service");
    if (!selectedPoste) errors.push("Qualification/Poste");
    if (!taux || parseFloat(taux) <= 0)
      errors.push(quantityPricing ? "Taux valide" : "Taux horaire valide");
    if (
      quantityPricing
        ? !quantity || Number(quantity) <= 0
        : !coefficient || Number(coefficient) <= 0
    )
      errors.push(quantityPricing ? "Quantité valide" : "Coefficient valide");
    if (
      selectedProduct === "Délégation" &&
      (!minimumHours || parseFloat(minimumHours) < 0)
    ) {
      errors.push("Heures minimum valides");
    }

    if (errors.length > 0) {
      message.error(`Champs requis : ${errors.join(", ")}`);
      return;
    }

    const newService = {
      type: "service",
      product: selectedProduct,
      poste: selectedPoste,
      taux: parseFloat(taux),
      ...(quantityPricing
        ? { quantity: Number(quantity) }
        : { coefficient: Number(coefficient) }),
      comment: comment.trim(),
      minimumHours:
        selectedProduct === "Délégation" ? parseFloat(minimumHours) : undefined,
    };

    setQuotation((prev) => ({
      ...prev,
      products: [...prev.products, newService],
    }));

    // Reset des champs
    setSelectedProduct("");
    setSelectedPoste("");
    setTaux("");
    setCoefficient("");
    setQuantity("1");
    setComment("");
    setMinimumHours("");

    message.success("Service ajouté au devis");
  };

  // Gestion des primes
  const addPrime = () => {
    const errors = [];
    if (!selectedPrimeType) errors.push("Type de prime");
    if (!primeDescription) errors.push("Description de la prime");
    if (!primeTaux || parseFloat(primeTaux) <= 0) errors.push("Taux valide");
    if (!primeCoefficient || parseFloat(primeCoefficient) <= 0)
      errors.push("Coefficient valide");

    if (errors.length > 0) {
      message.error(`Champs requis : ${errors.join(", ")}`);
      return;
    }

    const newPrime = {
      type: "Prime",
      product: selectedPrimeType,
      poste: primeDescription,
      taux: parseFloat(primeTaux),
      coefficient: parseFloat(primeCoefficient),
      comment: primeComment.trim(),
      isPrime: true, // Marqueur pour identifier les primes
    };

    setQuotation((prev) => ({
      ...prev,
      products: [...prev.products, newPrime],
    }));

    // Reset des champs
    setSelectedPrimeType("");
    setPrimeDescription("");
    setPrimeTaux("");
    setPrimeCoefficient("");
    setPrimeComment("");

    message.success("Prime ajoutée au devis");
  };

  const removeService = (index) => {
    const item = quotation.products[index];
    const itemType = item.isPrime ? "Prime supprimée" : "Service supprimé";

    setQuotation((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
    message.success(itemType);
  };

  // Calcul du total
  const calculateTotal = () => {
    return quotation.products
      .reduce((total, product) => {
        return total + lineAmount(product);
      }, 0)
      .toFixed(2);
  };

  // Sauvegarde du devis
  const saveQuotation = async () => {
    if (isLoading) return;
    const finalErrors = [2, 3, 4].flatMap(validateStep);
    if (finalErrors.length > 0) {
      message.error(`Impossible de créer le devis : ${finalErrors.join(", ")}`);
      return;
    }

    console.log(quotation);
    setIsLoading(true);
    try {
      const { data } = await axiosInstance.post("quotation", quotation);
      message.success("Devis créé avec succès");
      onCreated?.(data);
      closeModal();
    } catch (error) {
      console.error(error);
      if (error.response?.status === 403) {
        message.error("Accès non autorisé");
      } else if (error.response?.status === 400) {
        message.error("Données invalides");
      } else {
        message.error("Erreur lors de la création du devis");
      }
    }
    setIsLoading(false);
  };

  // Fermeture de la modal
  const closeModal = () => {
    setIsCreateQuotation(false);
    setCurrentStep(2);
    setQuotation({
      client: client,
      addressFacturation: "",
      addressLivraison: "",
      contact: "",
      products: [],
    });
    setSelectedProduct("");
    setSelectedPoste("");
    setTaux("");
    setCoefficient("");
    setQuantity("1");
    setComment("");
    setMinimumHours("");
  };

  const StepIndicator = () => (
    <ol className="mb-6 grid grid-cols-4 gap-2" aria-label="Étapes du devis">
      {[2, 3, 4, 5].map((step) => (
        <li
          key={step}
          aria-current={step === currentStep ? "step" : undefined}
          className={`rounded-lg border p-3 ${step === currentStep ? "border-blue-200 bg-blue-50 text-blue-800" : step < currentStep ? "border-slate-200 bg-white text-slate-700" : "border-slate-100 bg-slate-50 text-slate-400"}`}
        >
          <span className="mb-2 flex items-center gap-2 text-xs font-semibold">
            {step < currentStep ? (
              <CheckCircle size={15} />
            ) : (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
                {step - 1}
              </span>
            )}
            <span className="hidden sm:inline">{stepTitles[step - 1]}</span>
          </span>
          <span
            className={`block h-1 rounded-full ${step <= currentStep ? "bg-blue-600" : "bg-slate-200"}`}
          />
        </li>
      ))}
    </ol>
  );

  const stepTitles = [
    "Sélection du client",
    "Adresses",
    "Contact",
    "Services",
    "Primes & Finalisation",
  ];

  return (
    <Dialog
      open={isCreateQuotation}
      onOpenChange={(open) => {
        if (!open && !isLoading) closeModal();
      }}
    >
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-white overflow-hidden rounded-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-6 py-5">
          <DialogHeader className="pr-10">
            <DialogTitle className="flex items-center gap-3 text-xl font-semibold text-slate-900">
              <span className="rounded-xl bg-blue-50 p-3 text-blue-700">
                <FileText size={22} />
              </span>
              {initialQuotation
                ? `Nouvelle version du devis n° ${initialQuotation.quotationNumber ?? "Provisoire"}`
                : "Nouveau devis"}
            </DialogTitle>
            <DialogDescription className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <Building2 size={15} />
              {client?.companyName || client?.denomination || "Client"}
              {client?.siret && <span>· SIRET {client.siret}</span>}
            </DialogDescription>
          </DialogHeader>
          <button
            disabled={isLoading}
            onClick={closeModal}
            aria-label="Fermer le nouveau devis"
            className="absolute right-4 top-5 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <StepIndicator />

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-900">
              Étape {currentStep - 1} : {stepTitles[currentStep - 1]}
            </h3>
          </div>

          <div className="pb-6">
            {/* Étape 2: Adresses */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {initialQuotation && <p className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">Cette modification crée une nouvelle proposition avec un nouveau numéro. Le devis original reste conservé.</p>}
                <div className="max-w-sm space-y-2">
                  <Label htmlFor="quotation-validity">Date de validité du devis</Label>
                  <Input id="quotation-validity" type="date" value={String(quotation.expiryDate || '').slice(0,10)} onChange={event => setQuotation(current => ({...current, expiryDate:event.target.value}))} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="addressFacturation">
                      Adresse de facturation *
                    </Label>
                    <Select
                      value={quotation.addressFacturation}
                      onValueChange={(value) =>
                        setQuotation((prev) => ({
                          ...prev,
                          addressFacturation: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une adresse" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {clientAddresses.map((address, idx) => (
                          <SelectItem key={idx} value={address}>
                            {address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="addressLivraison">
                      Lieu de la mission *
                    </Label>
                    <Select
                      value={quotation.addressLivraison}
                      onValueChange={(value) =>
                        setQuotation((prev) => ({
                          ...prev,
                          addressLivraison: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner le lieu" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {clientAddresses.map((address, idx) => (
                          <SelectItem key={idx} value={address}>
                            {address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {quotation.addressFacturation && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-sm text-green-700">
                      <strong>Facturation:</strong>{" "}
                      {quotation.addressFacturation}
                    </p>
                  </div>
                )}

                {quotation.addressLivraison && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <strong>Mission:</strong> {quotation.addressLivraison}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Étape 3: Contact */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="contact">Contact principal *</Label>
                  <Select
                    value={quotation.contact}
                    onValueChange={(value) =>
                      setQuotation((prev) => ({ ...prev, contact: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un contact" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {clientContacts.map((contact, idx) => (
                        <SelectItem key={idx} value={contact}>
                          {contact}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {quotation.contact && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <h4 className="font-semibold text-green-800">
                          Contact sélectionné
                        </h4>
                        <p className="text-green-700">{quotation.contact}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">
                    Récapitulatif
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Client:</strong>{" "}
                      {client?.companyName || client?.denomination}
                    </p>
                    <p>
                      <strong>Facturation:</strong>{" "}
                      {quotation.addressFacturation}
                    </p>
                    <p>
                      <strong>Mission:</strong> {quotation.addressLivraison}
                    </p>
                    {quotation.contact && (
                      <p>
                        <strong>Contact:</strong> {quotation.contact}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Étape 4: Services */}
            {currentStep === 4 && (
              <div className="space-y-6">
                {/* Formulaire d'ajout de service */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-blue-700" />
                    Ajouter un service
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type de service *</Label>
                      <Select
                        value={selectedProduct}
                        onValueChange={setSelectedProduct}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un service" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {services.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Qualification/Poste *</Label>
                      <LazyLoadingSelectQualification
                        qualifications={qualifications}
                        handleChange={(name, value) => setSelectedPoste(value)}
                        formData={{ qualification: selectedPoste }}
                        setFormData={(data) =>
                          setSelectedPoste(data.qualification)
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>
                        {quantityPricing
                          ? "Taux / prix unitaire (€) *"
                          : "Taux horaire (€) *"}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="15.50"
                        value={taux}
                        onChange={(e) => setTaux(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {quantityPricing ? "Quantité *" : "Coefficient *"}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="1.5"
                        value={quantityPricing ? quantity : coefficient}
                        onChange={(e) =>
                          quantityPricing
                            ? setQuantity(e.target.value)
                            : setCoefficient(e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {quantityPricing ? "Total HT" : "Total horaire HT"}
                      </Label>
                      <div className="border rounded p-2 h-10 flex items-center bg-white font-semibold text-blue-700">
                        {taux && (quantityPricing ? quantity : coefficient)
                          ? `${(Number(taux) * Number(quantityPricing ? quantity : coefficient)).toFixed(2)} €`
                          : "0.00 €"}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Commentaire</Label>
                    <Input
                      placeholder="Ex : Facturation à 30 jours, conditions particulières..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </div>

                  {selectedProduct === "Délégation" && (
                    <div className="space-y-2 bg-yellow-50 p-3 rounded border border-yellow-200">
                      <Label>
                        Heures minimum avant embauche sans facturation *
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="40"
                        value={minimumHours}
                        onChange={(e) => setMinimumHours(e.target.value)}
                      />
                      <p className="text-xs text-yellow-700">
                        Nombre d'heures travaillées avant que l'embauche soit
                        gratuite
                      </p>
                    </div>
                  )}

                  <div className="flex justify-center">
                    <Button
                      type="button"
                      onClick={addService}
                      className="bg-blue-700 text-white hover:bg-blue-800 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter au devis
                    </Button>
                  </div>
                </div>

                {/* Liste des services et primes ajoutés */}
                {quotation.products.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-700" />
                        Services & Primes ajoutés ({quotation.products.length})
                      </h4>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Total du devis</p>
                        <p className="text-xl font-bold text-blue-700">
                          {calculateTotal()} €
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {quotation.products.map((product, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center justify-between border rounded-lg shadow-sm p-4 ${
                            product.isPrime
                              ? "bg-yellow-50 border-yellow-200"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`text-xs font-medium px-2 py-1 rounded ${
                                  product.isPrime
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-indigo-100 text-indigo-800"
                                }`}
                              >
                                {product.isPrime ? "🎁" : "⚙️"}{" "}
                                {product.product}
                              </span>
                              <span className="text-sm font-medium text-gray-800">
                                {product.poste}
                              </span>
                            </div>

                            {product.comment && (
                              <p className="text-sm text-gray-600 mb-1">
                                💬 {product.comment}
                              </p>
                            )}

                            {product.minimumHours && (
                              <p className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded w-fit">
                                ⏱️ Heures min. avant embauche :{" "}
                                {product.minimumHours}h
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-600">
                                {product.taux}€ × {pricingMultiplier(product)}{" "}
                                {usesQuantity(product) ? "unité(s)" : "(coef.)"}
                              </p>
                              <p className="text-lg font-semibold text-blue-700">
                                {lineAmount(product).toFixed(2)}€
                              </p>
                            </div>
                            <Button
                              type="button"
                              onClick={() => removeService(idx)}
                              className="p-2 h-auto bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border-red-200"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Étape 5: Primes et Finalisation */}
            {currentStep === 5 && (
              <div className="space-y-6">
                {/* Formulaire d'ajout de prime */}
                <div className="bg-yellow-50 p-4 rounded-lg space-y-4 border border-yellow-200">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Gift className="w-5 h-5 text-yellow-600" />
                    Ajouter une prime (optionnel)
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Type de prime */}
                    <div className="space-y-2">
                      <Label>Type de prime *</Label>
                      <Select
                        value={selectedPrimeType}
                        onValueChange={setSelectedPrimeType}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un type de prime" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {TYPES_PRIMES.map((prime) => (
                            <SelectItem key={prime} value={prime}>
                              {prime}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Input
                        value={primeDescription}
                        onChange={(e) => setPrimeDescription(e.target.value)}
                        placeholder="Description de la prime"
                      />
                    </div>

                    {/* Taux */}
                    <div className="space-y-2">
                      <Label>Taux (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={primeTaux}
                        onChange={(e) => setPrimeTaux(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>

                    {/* Coefficient */}
                    <div className="space-y-2">
                      <Label>
                        {quantityPricing ? "Quantité *" : "Coefficient *"}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={primeCoefficient}
                        onChange={(e) => setPrimeCoefficient(e.target.value)}
                        placeholder="1.00"
                      />
                    </div>

                    {/* Commentaire */}
                    <div className="space-y-2 md:col-span-2">
                      <Label>Commentaire</Label>
                      <Input
                        value={primeComment}
                        onChange={(e) => setPrimeComment(e.target.value)}
                        placeholder="Commentaire optionnel"
                      />
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <Button
                      type="button"
                      onClick={addPrime}
                      className="bg-yellow-600 text-white hover:bg-yellow-700 flex items-center gap-2"
                    >
                      <Gift className="w-4 h-4" />
                      Ajouter la prime
                    </Button>
                  </div>
                </div>

                {/* Boutons de prévisualisation et téléchargement */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Eye className="w-5 h-5 text-blue-600" />
                    Prévisualisation PDF
                  </h4>
                  <div className="flex justify-center gap-3">
                    <Button
                      type="button"
                      onClick={generatePreview}
                      disabled={
                        isGeneratingPreview || quotation.products.length === 0
                      }
                      className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isGeneratingPreview ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Génération...
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          Visualiser PDF
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      onClick={downloadPreviewPDF}
                      disabled={
                        isDownloadingPDF || quotation.products.length === 0
                      }
                      className="bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isDownloadingPDF ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Téléchargement...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          Télécharger PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Récapitulatif final */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Récapitulatif du devis
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Client:</span>
                      <span className="font-medium">
                        {client?.companyName || client?.denomination}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Services:</span>
                      <span className="font-medium">
                        {quotation.products.filter((p) => !p.isPrime).length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Primes:</span>
                      <span className="font-medium">
                        {quotation.products.filter((p) => p.isPrime).length}
                      </span>
                    </div>
                    <div className="border-t pt-2 flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-blue-700">{calculateTotal()}€</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Boutons de navigation */}
          <div className="sticky bottom-0 -mx-6 -mb-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <div className="flex gap-3">
              {currentStep > 2 && (
                <Button
                  type="button"
                  onClick={prevStep}
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Précédent
                </Button>
              )}
            </div>

            <div className="text-sm text-gray-600">
              Étape {currentStep - 1} sur {totalSteps - 1}
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={closeModal}
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              >
                Annuler
              </Button>

              {currentStep < totalSteps ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="bg-blue-700 text-white hover:bg-blue-800 flex items-center gap-2"
                >
                  Suivant
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={saveQuotation}
                  disabled={isLoading || quotation.products.length === 0}
                  className="bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Créer le devis
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};



export default freshModal(CreateQuotationModalFormSession, "isCreateQuotation");
